/**
 * @file Asset detail Maintenance section.
 */

import { appData, state } from "../../app/state/state.js";
import { canAccessView } from "../../app/routing/boardAccess.js";
import { getResolvedCardDate } from "../../shared/board/itemSortUtils.js";
import { isItemClosed } from "../../shared/utils/closedState.js";
import {
  convertIdsToNamesForEditing,
  escapeHTML,
} from "../../shared/utils/contentUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import {
  isColorDark,
} from "../../shared/utils/displayUtils.js";
import {
  getUserDisplayNameById,
} from "../../shared/utils/userDisplay.js";

const STREAM_ORDER = Object.freeze({
  issue: 0,
  service: 1,
});

const STREAM_META = Object.freeze({
  issue: Object.freeze({
    icon: "report",
    label: "Issue",
    color: "#d65f5f",
    fillColor: "#fde3e3",
  }),
  service: Object.freeze({
    icon: "build_circle",
    label: "Service",
    color: "#4599e8",
    fillColor: "#e4f2ff",
  }),
});

const CLOSED_MAINTENANCE_SUMMARY_BUTTON_CLASSES = [
  "closed-asset-maintenance-summary",
  "mt-2",
  "flex",
  "min-h-8",
  "w-full",
  "items-center",
  "gap-1.5",
  "text-left",
  "text-sm",
  "transition-colors",
  "focus:outline-none",
  "focus:text-[var(--dfc-brand-primary)]",
].join(" ");

const ASSET_MAINTENANCE_UI_SESSION = {
  assetId: null,
  closedExpanded: false,
};

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function canUseMaintenanceDetail() {
  return canAccessView(state.currentUserProfile, "maintenance");
}

function getMaintenanceDefaults(asset, stream) {
  const defaults = {
    linked_asset_id: asset.id,
    linked_asset_name: asset.name || asset.item || "",
    linked_asset_serial_number: asset.serial_number || "",
    linked_asset_types: Array.isArray(asset.types) ? asset.types : [],
    parentId: asset.id,
  };
  if (stream) {
    defaults.stream = stream;
  }
  return defaults;
}

function getLinkedMaintenanceItems(asset) {
  if (!asset?.id) {return [];}
  return (appData?.maintenance || [])
    .filter((item) => item?.linked_asset_id === asset.id);
}

function getConfigEntry(source = [], id) {
  return (Array.isArray(source) ? source : [])
    .find((entry) => entry?.id === id) || {};
}

function getMaintenanceTypeLabel(item) {
  const typeConfig = appData?.config?.maintenanceTypes || [];
  const labels = (Array.isArray(item?.types) ? item.types : [])
    .map((id) => getConfigEntry(typeConfig, id)?.name || id)
    .filter(Boolean);
  return labels.length > 0 ? labels.join(", ") : "Maintenance";
}

function getStatusConfig(item) {
  return getConfigEntry(appData?.config?.maintenanceStatus || [], item?.status);
}

function getPriorityConfig(item) {
  return getConfigEntry(appData?.config?.priorities || [], item?.priority);
}

function getStreamMeta(stream) {
  return STREAM_META[stream] || {
    icon: "construction",
    label: stream || "Maintenance",
    color: "#64748b",
    fillColor: "transparent",
  };
}

function renderStreamIcon(stream, className = "") {
  return `
    <span
      class="maintenance-stream-icon-colored flex-shrink-0 leading-none ${className}"
      style="--maintenance-stream-outline: ${attrValue(stream.color)}; --maintenance-stream-fill: ${attrValue(stream.fillColor)};"
      title="${attrValue(stream.label)}"
      aria-label="${attrValue(stream.label)}"
    >
      <span class="maintenance-stream-icon-colored__fill material-symbols-outlined" aria-hidden="true">${stream.icon}</span>
      <span class="maintenance-stream-icon-colored__outline material-symbols-outlined">${stream.icon}</span>
    </span>
  `;
}

function renderMobileStreamBadge(stream) {
  return `
    <span class="inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center" title="${attrValue(stream.label)}" aria-label="${attrValue(stream.label)}">
      ${renderStreamIcon(stream, "text-[17px]")}
    </span>
  `;
}

function compareMaintenanceItems(a, b) {
  const streamDiff =
    (STREAM_ORDER[a?.stream] ?? 99) - (STREAM_ORDER[b?.stream] ?? 99);
  if (streamDiff !== 0) {return streamDiff;}

  const closedDiff = Number(isItemClosed(a)) - Number(isItemClosed(b));
  if (closedDiff !== 0) {return closedDiff;}

  const dateA = getResolvedCardDate("maintenance", a, appData?.tasks || []);
  const dateB = getResolvedCardDate("maintenance", b, appData?.tasks || []);
  const timeA = dateA ? new Date(dateA).getTime() : Number.POSITIVE_INFINITY;
  const timeB = dateB ? new Date(dateB).getTime() : Number.POSITIVE_INFINITY;
  if (timeA !== timeB) {return timeA - timeB;}

  return String(a?.item || a?.id || "")
    .localeCompare(String(b?.item || b?.id || ""));
}

function getAssetMaintenanceUiSession(assetId) {
  if (ASSET_MAINTENANCE_UI_SESSION.assetId !== assetId) {
    ASSET_MAINTENANCE_UI_SESSION.assetId = assetId;
    ASSET_MAINTENANCE_UI_SESSION.closedExpanded = false;
  }
  return ASSET_MAINTENANCE_UI_SESSION;
}

function renderCreateButton(asset) {
  if (!asset?.id || !canUseMaintenanceDetail()) {return "";}
  return `
    <button
      type="button"
      class="asset-maintenance-create-btn icon-action-btn"
      title="Add Maintenance"
      aria-label="Add Maintenance"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
      </svg>
    </button>
  `;
}

function getLatestNoteText(item) {
  const note = item?.latestNote;
  if (!note) {return "";}
  return note.content || note.text || "";
}

function getFirstDisplayName(name = "") {
  return String(name || "").trim().split(/\s+/)[0] || "";
}

function renderMetadataValue(icon, text, title = "") {
  return `
    <span class="text-gray-500 dark:text-gray-400 flex items-center min-w-0 truncate" title="${attrValue(title || text || "None")}">
      <span class="material-symbols-outlined text-[14px] mr-1 opacity-70 flex-shrink-0">${icon}</span>
      <span class="truncate">${escapeHTML(text || "None")}</span>
    </span>
  `;
}

function renderTypeBadge(item) {
  const typeIds = Array.isArray(item?.types) ? item.types : [];
  if (typeIds.length === 0) {return "";}
  const typeConfig = appData?.config?.maintenanceTypes || [];
  const primaryType = getConfigEntry(typeConfig, typeIds[0]);
  if (!primaryType?.id) {return "";}
  const backgroundColor = primaryType.color || "#e5e7eb";
  const textColor = isColorDark(backgroundColor) ? "white" : "#1f2937";
  return `
    <span class="rounded px-2 py-0.5 text-xs sm:ml-2 flex-shrink-0" style="background-color: ${attrValue(backgroundColor)}; color: ${textColor};" title="${attrValue(getMaintenanceTypeLabel(item))}">
      ${escapeHTML(primaryType.name || typeIds[0])}
    </span>
  `;
}

function renderStatusBadge(item) {
  const status = getStatusConfig(item);
  const statusName = status?.name || item?.status || "No status";
  const statusColor = status?.color || "#e5e7eb";
  const statusTextColor = isColorDark(statusColor) ? "text-white" : "text-gray-800";

  return `
    <span class="rounded px-2 py-0.5 text-xs font-medium ${statusTextColor} text-center truncate" style="background-color: ${attrValue(statusColor)};" title="Status: ${attrValue(statusName)}">
      ${escapeHTML(statusName)}
    </span>
  `;
}

function getIncompleteChecklistItems(item) {
  if (!item?.checklist || typeof item.checklist !== "object") {return [];}
  return Object.entries(item.checklist)
    .map(([id, data]) => ({ id, ...data }))
    .filter((checklistItem) => !checklistItem.isCompleted)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

function renderChecklistPreview(item) {
  const nextItem = getIncompleteChecklistItems(item)[0];
  if (!nextItem) {return "";}
  const text = nextItem.text || "";

  return `
    <div class="flex items-center min-w-0" title="Next action: ${attrValue(text)}">
      <input
        type="checkbox"
        class="kanban-checklist-toggle form-checkbox h-3.5 w-3.5 rounded border-gray-300 text-[var(--dfc-brand-primary)] focus:ring-[var(--dfc-brand-primary-ring)] cursor-pointer flex-shrink-0"
        data-maintenance-id="${attrValue(item.id)}"
        data-item-id="${attrValue(nextItem.id)}"
        onclick="event.stopPropagation(); window.handleKanbanChecklistToggle(event, '${attrValue(item.id)}', 'maintenance', '${attrValue(nextItem.id)}')"
      >
      <span class="ml-1.5 text-xs text-gray-500 dark:text-gray-400 truncate">${escapeHTML(text)}</span>
    </div>
  `;
}

function renderLatestNotePreview(item) {
  const rawText = getLatestNoteText(item);
  if (!rawText) {return "";}
  const textWithNames = convertIdsToNamesForEditing(rawText);
  const snippet = textWithNames.substring(0, 150) +
    (textWithNames.length > 150 ? "..." : "");

  return `
    <div class="flex items-center min-w-0 truncate text-gray-500 dark:text-gray-400" title="${attrValue(textWithNames)}">
      <span class="material-symbols-outlined text-[14px] mr-1.5">chat_bubble</span>
      <span class="text-xs truncate italic">${escapeHTML(snippet)}</span>
    </div>
  `;
}

function renderMaintenanceItem(item) {
  const stream = getStreamMeta(item?.stream);
  const priority = getPriorityConfig(item);
  const resolvedDate = getResolvedCardDate("maintenance", item, appData?.tasks || []);
  const dateText = formatDate(resolvedDate);
  const managedByName = item?.managed_by ?
    getUserDisplayNameById(item.managed_by) :
    "";
  const managedByFirstName = getFirstDisplayName(managedByName);
  const title = item?.item || getMaintenanceTypeLabel(item);
  const priorityColor = priority?.color || "#e5e7eb";
  const closedClass = isItemClosed(item) ? "opacity-75" : "";
  const typeBadgeHtml = renderTypeBadge(item);
  const statusBadgeHtml = renderStatusBadge(item);
  const checklistHtml = renderChecklistPreview(item);
  const latestNoteHtml = renderLatestNotePreview(item);

  return `
    <div
      class="asset-maintenance-item group p-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 cursor-pointer transition-colors shadow-sm bg-white dark:bg-gray-800 text-sm ${closedClass}"
      data-maintenance-id="${attrValue(item.id)}"
    >
      <div class="space-y-3 sm:hidden">
        <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 items-start">
            <span class="mt-1.5 mr-2.5 h-2.5 w-2.5 rounded-full flex-shrink-0" style="background-color: ${attrValue(priorityColor)};" title="Urgency: ${attrValue(priority?.name || "N/A")}"></span>
            <span class="font-medium leading-5 text-gray-900 dark:text-gray-100 break-words min-w-0" title="${attrValue(title)}">${escapeHTML(title)}</span>
          </div>
          ${renderMobileStreamBadge(stream)}
        </div>
        <div class="space-y-2 text-xs">
          ${typeBadgeHtml ? `<div class="flex min-w-0 items-center overflow-hidden">${typeBadgeHtml}</div>` : ""}
          <div class="flex min-w-0 items-center overflow-hidden">${statusBadgeHtml}</div>
          ${renderMetadataValue("calendar_today", dateText || "No date", resolvedDate || "None")}
          ${renderMetadataValue("person", managedByFirstName || "Unknown", managedByName || "Unknown")}
        </div>
      </div>

      <div class="hidden sm:flex sm:items-center">
        <div class="flex items-center w-[300px] flex-shrink-0 mr-4 min-w-0">
          <span class="w-2.5 h-2.5 rounded-full mr-2.5 flex-shrink-0" style="background-color: ${attrValue(priorityColor)};" title="Urgency: ${attrValue(priority?.name || "N/A")}"></span>
          ${renderStreamIcon(stream, "mr-2 text-[16px]")}
          <span class="font-medium text-gray-900 dark:text-gray-100 truncate" title="${attrValue(title)}">${escapeHTML(title)}</span>
        </div>

        <div class="grid grid-cols-[100px_120px_200px_200px] gap-3 items-center flex-shrink-0 mr-4 text-xs border-r border-gray-100 dark:border-gray-700 pr-4">
          ${renderMetadataValue("calendar_today", dateText || "No date", resolvedDate || "None")}
          ${renderMetadataValue("person", managedByFirstName || "Unknown", managedByName || "Unknown")}
          <div class="truncate flex items-center justify-start">${typeBadgeHtml}</div>
          <div class="truncate flex items-center justify-start">${statusBadgeHtml}</div>
        </div>

        <div class="flex items-center flex-grow min-w-0 space-x-12">
          ${checklistHtml ? `<div class="flex-shrink-0 min-w-0 truncate max-w-[40%]">${checklistHtml}</div>` : ""}
          ${latestNoteHtml ? `<div class="flex-shrink min-w-0 truncate flex-grow">${latestNoteHtml}</div>` : ""}
        </div>
      </div>
    </div>
  `;
}

function renderClosedMaintenanceByStream(closedItems) {
  return ["issue", "service"].map((stream) => {
    const streamItems = closedItems.filter((item) => item?.stream === stream);
    if (streamItems.length === 0) {return "";}
    const streamMeta = getStreamMeta(stream);
    const heading = stream === "issue" ? "Issues" : "Service";
    return `
      <div class="closed-asset-maintenance-stream-group">
        <div class="mb-2 mt-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-normal text-gray-500 dark:text-gray-400">
          ${renderStreamIcon(streamMeta, "text-[15px]")}
          <span>${heading}</span>
        </div>
        ${streamItems.map(renderMaintenanceItem).join("")}
      </div>
    `;
  }).join("");
}

function renderMaintenanceItemsBody(asset, openItems, closedItems) {
  const uiSession = getAssetMaintenanceUiSession(asset.id);
  const closedExpanded = uiSession.closedExpanded;
  const closedSummaryClasses = closedExpanded
    ? `${CLOSED_MAINTENANCE_SUMMARY_BUTTON_CLASSES} text-[var(--dfc-brand-accent)]`
    : `${CLOSED_MAINTENANCE_SUMMARY_BUTTON_CLASSES} text-gray-500 hover:text-[var(--dfc-brand-primary)] dark:text-gray-400`;

  if (openItems.length === 0 && closedItems.length === 0) {
    return '<div class="mb-4"></div>';
  }

  return `
    <div class="mb-4">
      ${openItems.map(renderMaintenanceItem).join("")}
    </div>
    ${closedItems.length > 0 ? `
      <button type="button" class="${closedSummaryClasses}" aria-expanded="${closedExpanded ? "true" : "false"}">
        <span class="material-symbols-outlined text-[18px] leading-none">${closedExpanded ? "expand_less" : "expand_more"}</span>
        <span>${closedItems.length} closed Maintenance ${closedItems.length === 1 ? "item" : "items"}</span>
      </button>
    ` : ""}
    ${closedItems.length > 0 && closedExpanded ? `
      <div class="closed-asset-maintenance opacity-80">
        ${renderClosedMaintenanceByStream(closedItems)}
      </div>
    ` : ""}
  `;
}

/**
 * Renders the Asset detail Maintenance section.
 * @param {object} asset Asset record.
 * @returns {string} Section HTML.
 */
export function renderAssetMaintenanceSection(asset) {
  const linkedItems = getLinkedMaintenanceItems(asset)
    .sort(compareMaintenanceItems);
  const openItems = linkedItems.filter((item) => !isItemClosed(item));
  const closedItems = linkedItems.filter((item) => isItemClosed(item));
  const canCreate = canUseMaintenanceDetail();

  return `
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-xl font-semibold dark:text-gray-100">Maintenance</h2>
        ${canCreate ? renderCreateButton(asset) : ""}
      </div>
      <div class="mt-4">
        ${renderMaintenanceItemsBody(asset, openItems, closedItems)}
      </div>
    </div>
  `;
}

/**
 * Attaches Asset detail Maintenance section listeners.
 * @param {HTMLElement} detailsViewContainer Detail root.
 * @param {object} asset Asset record.
 * @param {object} context Detail adapter context.
 * @returns {void}
 */
export function attachAssetMaintenanceSectionListeners(
  detailsViewContainer,
  asset,
  context = {},
) {
  if (!detailsViewContainer || !asset?.id) {return;}

  detailsViewContainer
    .querySelectorAll(".asset-maintenance-create-btn")
    .forEach((button) => {
      button.onclick = async (event) => {
        event.stopPropagation();
        const { openMaintenanceCreateFlow } = await import(
          "../maintenance/maintenanceFormFields.js"
        );
        openMaintenanceCreateFlow(getMaintenanceDefaults(asset));
      };
    });

  const closedSummary = detailsViewContainer.querySelector(
    ".closed-asset-maintenance-summary",
  );
  if (closedSummary) {
    closedSummary.onclick = (event) => {
      event.stopPropagation();
      const uiSession = getAssetMaintenanceUiSession(asset.id);
      uiSession.closedExpanded = !uiSession.closedExpanded;
      context.renderDetailsView?.();
    };
  }

  detailsViewContainer
    .querySelectorAll(".asset-maintenance-item")
    .forEach((row) => {
      row.onclick = () => {
        const maintenanceId = row.dataset.maintenanceId;
        if (!maintenanceId || !canUseMaintenanceDetail()) {return;}
        context.showDetails?.(maintenanceId, "maintenance");
      };
    });
}
